import pdfMake from 'pdfmake/build/pdfmake.js';
import {
  ReportImageRequestDTO,
  SaveImageRequestDTO,
  WellDetailsState,
} from '../../business/models/Models';
import Logo from 'Icons/RMlogo.png';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import { isNullOrUndefined } from 'utils/Global';
import WellService from 'business/services/WellService';
import PlateService from 'business/services/PlateService';
import { EReportTypes } from 'enums/EIconPanels';
import {
  createPdfWithPopupBlockerHandler,
  getScreenNameColumnsTable,
  getScreenNamesFromLayers,
  roundToMaxDecimals,
  INSTANT_DROP_SCREEN_NAME_SHORT_MAX_LENGTH,
  INSTANT_DROP_SCREEN_NAME_MEDIUM_MAX_LENGTH,
} from './utils';
import { WellNavigationDetails } from '../Experiment/constants';
import { fonts } from './ReportsConstants';

pdfMake.fonts = { Roboto: fonts.Roboto };
const plateService = new PlateService();
const wellService = new WellService();

function dateTimeConverter(imagedDate) {
  const rawDate = new Date(imagedDate);
  const year = rawDate.getFullYear();
  const month = rawDate.getMonth() + 1;
  const day = rawDate.getDate();
  let hour = rawDate.getHours();
  const ampm = hour < 12 ? 'AM' : 'PM';
  hour = hour % 12;
  const minute = rawDate.getMinutes();

  let finalDate =
    String(year) +
    '-' +
    String(month).padStart(2, '0') +
    '-' +
    String(day).padStart(2, '0');
  finalDate =
    finalDate +
    ' ' +
    String(hour).padStart(2, '0') +
    ':' +
    String(minute).padStart(2, '0') +
    ' ' +
    ampm;

  return finalDate;
}

function getHitsByIngredients(selectedWell: any[], docData, selectedPlateId: number) {
  const data = [];
  for (const wl of selectedWell) {
    const plateInfo: any = docData.plateDto.find(
      (plate) => plate.plateId === selectedPlateId,
    );
    const selectedWellInfo = plateInfo.ingredients.wellList.find(
      (well) => well.wellNumber === wl.wellNum,
    );

    selectedWellInfo.wellLayerList.forEach((wlist) => {
      wlist.wellLayerIngredientList.forEach((wellIng) => {
        data.push({
          ingredientsName: wellIng.ingredientStockIngredient.longName,
          concentrationRange: wellIng.concentration,
          phRange: wellIng.pH === null ? '-' : wellIng.pH,
          unitRange: wellIng.ingredientStock.concentrationUnits,
          preMixedConcentration: wellIng.preMixedConcentration,
          pH: wellIng.pH,
        });
      });
    });
  }

  const tableData: any[] = [];
  data.forEach((dt) => {
    if (
      tableData.length > 0 &&
      tableData.some((md) => md.ingredientsName === dt.ingredientsName)
    ) {
      const index: number = tableData.findIndex(function (tdata) {
        return tdata.ingredientsName === dt.ingredientsName;
      });
      tableData[index].count = tableData[index].count + 1;
      if (dt.preMixedConcentration < tableData[index].minConc) {
        tableData[index].minConc = dt.preMixedConcentration;
      }
      if (dt.preMixedConcentration > tableData[index].maxConc) {
        tableData[index].maxConc = dt.preMixedConcentration;
      }
      if (dt.pH) {
        if (dt.pH < tableData[index].minPH) {
          tableData[index].minPH = dt.pH;
        }
        if (dt.pH > tableData[index].maxPH) {
          tableData[index].maxPH = dt.pH;
        }
      }
    } else {
      let ingredient = {} as any;
      ingredient = dt;
      ingredient.minConc = dt.preMixedConcentration;
      ingredient.maxConc = dt.preMixedConcentration;
      ingredient.count = 1;
      if (!dt.pH) {
        ingredient.minPH = 0;
        ingredient.maxPH = 0;
      } else {
        ingredient.minPH = dt.pH;
        ingredient.maxPH = dt.pH;
      }
      tableData.push(ingredient);
    }
  });

  /* Construct table Body Header */
  const tableBody: any[] = [
    [
      { text: 'Ingredients', fillColor: '#cccccc' },
      { text: 'No. of Hits', fillColor: '#cccccc', alignment: 'center' },
      { text: 'pH Range', fillColor: '#cccccc', alignment: 'center' },
      { text: 'Concentration Range', fillColor: '#cccccc', alignment: 'center' },
    ],
  ];

  /* Construct table Body Detail */
  for (let index = 0; index < tableData.length; index++) {
    tableBody.push([
      { text: tableData[index].ingredientsName },
      { text: tableData[index].count, alignment: 'center' },
      {
        text:
          tableData[index].maxPH - tableData[index].minPH > 0
            ? tableData[index].minPH + ' - ' + tableData[index].maxPH
            : tableData[index].minPH === 0
            ? '-'
            : tableData[index].minPH,
        alignment: 'center',
      },
      {
        text:
          tableData[index].maxConc - tableData[index].minConc > 0
            ? roundToMaxDecimals(tableData[index].minConc) +
              ' - ' +
              roundToMaxDecimals(tableData[index].maxConc) +
              ' ' +
              tableData[index].unitRange
            : roundToMaxDecimals(tableData[index].minConc) +
              ' ' +
              tableData[index].unitRange,
        alignment: 'center',
      },
    ]);
  }

  return {
    table: {
      widths: ['40%', '20%', '20%', '20%'],
      body: tableBody,
    },
    //layout: TABLE_LAYOUT,
  };
}

async function fetchPlatesScores(plateIds: number[]) {
  const allScoresLookup = {};

  for (const plateId of plateIds) {
    const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(plateId);

    const plateScoresLookup = {};

    plateScores?.forEach((ps) => {
      ps.forEach((score) => {
        const key = `${score.wellNumber}-${score.dropNumber}-${score.scoreBatchId}`;
        if (!plateScoresLookup[key]) {
          plateScoresLookup[key] = [];
        }
        plateScoresLookup[key].push(score);
      });
    });

    allScoresLookup[plateId] = plateScoresLookup;
  }
  return allScoresLookup;
}

async function fetchDataHitsByExperiment(
  selectedWellParam,
  docData,
  dropsChecked,
  selectedDropImageParameters,
  selectedPlateId: number,
) {
  const dropNumber = [];
  dropsChecked.forEach((drop) => {
    if (drop['checked']) {
      dropNumber.push(drop['dropNumber']);
    }
  });
  const plateIds = [
    ...new Set(docData.plateDto.map((plate) => plate.plateId)),
  ] as number[];

  const allScoresLookup = await fetchPlatesScores(plateIds);

  const selectedWells: any = [];
  selectedWellParam.forEach((wellParam) => {
    const well = {
      wellNum: wellParam.wellNum,
      inspectionSet: {
        id: selectedDropImageParameters.inspection.id,
      },
      plateID: wellParam.plateID,
      cpID: selectedDropImageParameters.cpId,
    };
    selectedWells.push(well);
  });

  const selectedCaptureProfile = docData.profiles.find(
    (cp) => cp.id == selectedWells[0].cpID,
  );
  const wellDetails: WellDetailsState[] = [];
  const contextMap = new Map<string, any>();

  for (const wl of selectedWells) {
    const plateInfo: any = docData.plateDto.find(
      (plate) => plate.plateId === selectedPlateId,
    );
    const selectedWellInfo = plateInfo.ingredients.wellList.find(
      (well) => well.wellNumber === wl.wellNum,
    );
    const selectedInspection = docData.inspections.find(
      (ins) => ins.id === wl.inspectionSet?.id,
    );
    const selectedGroupedInspection = selectedInspection?.groupedInspections?.find(
      (gp) => gp.plateId === selectedPlateId,
    );

    const wellIngredientsList: any[] = [];
    selectedWellInfo.wellLayerList.forEach((wlist) => {
      wlist.wellLayerIngredientList.forEach((wellIng) => {
        wellIngredientsList.push({
          ingredientsName: wellIng.ingredientStockIngredient.longName,
          concentrationRange: wellIng.concentration,
          phRange: wellIng.pH === null ? '-' : wellIng.pH,
          unitRange: wellIng.ingredientStock.concentrationUnits,
          preMixedConcentration: wellIng.preMixedConcentration,
        });
      });
    });

    for (const drop of dropNumber) {
      const selectedDrop = selectedWellInfo.dropList.find(
        (drp) => drp.dropNumber === drop,
      );
      if (!selectedDrop) {
        continue;
      }

      const proteinFormulation =
        plateInfo.ingredients.proteinFormulation?.[selectedDrop.proteinFormulationID];
      if (!proteinFormulation) {
        continue;
      }

      const regionId = getRegionId(
        docData.regions,
        wl.wellNum,
        selectedDrop.dropNumber,
        'Drop',
        plateInfo.plateId,
      );
      const wellDropTitle = `${selectedWellInfo.rowLetter}${selectedWellInfo.columnNumber}-${drop}`;

      const wellDetail = Object.assign(new WellDetailsState(), {
        cpID: selectedCaptureProfile.id,
        dropNumber: selectedDrop.dropNumber,
        imageBatchId: selectedGroupedInspection?.imageBatchId,
        plateID: plateInfo.plateId,
        regionID: regionId,
        regionName: wl.regionName,
        wellNum: wl.wellNum,
      });

      wellDetails.push(wellDetail);

      const key = `${wl.wellNum}-${drop}-${selectedGroupedInspection?.imageBatchId}`;
      contextMap.set(key, {
        plateId: plateInfo.plateId,
        wellNum: wl.wellNum,
        dropNumber: drop,
        batchId: selectedGroupedInspection?.imageBatchId,
        wellIngredientsList,
        proteinFormulation,
        wellDropTitle,
        dateImaged: selectedInspection.dateImaged,
        dropProteinVolume: selectedDrop.dropProteinVolume,
        dropWellVolume: selectedDrop.dropWellVolume,
      });
    }
  }

  const imageDto = new SaveImageRequestDTO({
    saveAllRegions: true,
    saveAllWellDropScores: false,
    saveAnnotations: true,
    selectedCaptureProfileIds: [selectedCaptureProfile.id],
    inspectionSetDTOs: [],
    saveAllDrops: false,
    saveAllFocusLevels: false,
    saveAllInspections: false,
    wellDetails: wellDetails,
    saveCurrentZoom: false,
    saveIngredientInfo: false,
  });

  const reportImageRequest = new ReportImageRequestDTO();
  reportImageRequest.reportType = EReportTypes.InstantDropReport;
  reportImageRequest.saveImageRequestDTO = imageDto;

  const imageBase64List = await wellService.getWellImagesWithDrawingsAnnotations(
    reportImageRequest,
  );

  const tableData: any[] = [];
  for (const contextKey of contextMap.keys()) {
    const context = contextMap.get(contextKey);
    const key = `${context.wellNum}-${context.dropNumber}-${context.batchId}`;
    const scores = allScoresLookup[context.plateId]?.[key] || [];
    const imageBase64 = imageBase64List.find(
      (x) => x.key === `${context.plateId}-${key}`,
    );

    if (imageBase64?.wellImage && context.proteinFormulation) {
      tableData.push({
        wellIngredientsList: context.wellIngredientsList,
        proteinFormulation: context.proteinFormulation,
        dropImage: imageBase64,
        wellDropTitle: context.wellDropTitle,
        dropProteinVolume: context.dropProteinVolume,
        dropWellVolume: context.dropWellVolume,
        dateImaged: context.dateImaged,
        scores,
      });
    }
  }

  const tableBody: any[] = [];

  if (
    imageBase64List?.length > 0 &&
    imageBase64List.some((image) => !isNullOrUndefined(image.key))
  ) {
    tableBody.push([
      { text: 'Drop Info', fillColor: '#cccccc' },
      { text: selectedCaptureProfile?.name, fillColor: '#cccccc' },
    ]);
    /* Construct table Body Detail */

    for (const dataRow of tableData) {
      let scoreString = '';
      if (dataRow.scores) {
        for (let i = 0; i < dataRow.scores.length; i++) {
          scoreString =
            scoreString +
            dataRow.scores[i].scoreName +
            '(' +
            dataRow.scores[i].hotKey +
            ')' +
            ' ';
        }
      }

      const ingredientsBody: any[] = [];
      if (dataRow.wellIngredientsList.length === 0) {
        ingredientsBody.push([{ border: [false, false, false, false], text: '' }]);
      } else {
        for (const wellIng of dataRow.wellIngredientsList) {
          ingredientsBody.push([
            {
              border: [false, false, false, false],
              text:
                roundToMaxDecimals(wellIng.preMixedConcentration) +
                ' ' +
                wellIng.unitRange +
                ' ' +
                wellIng.ingredientsName,
            },
          ]);
        }
      }

      tableBody.push([
        {
          stack: [
            {
              text: dataRow.wellDropTitle,
              style: 'largeText',
              bold: true,
              margin: [5, 10, 0, 10],
            },
            {
              text: [
                { text: 'Sample: ', bold: true },
                { text: dataRow.proteinFormulation.proteinInfo.name },
              ],
              margin: [5, 5, 0, 5],
            },
            {
              text: [
                { text: 'Sample / Well Volume: ', bold: true },
                roundToMaxDecimals(dataRow.dropProteinVolume) +
                  ' µL ' +
                  ' / ' +
                  roundToMaxDecimals(dataRow.dropWellVolume) +
                  ' µL ',
              ],
              margin: [5, 5, 0, 5],
            },
            {
              columns: [
                {
                  text: 'Scores:',
                  bold: true,
                  width: '12%',
                },
                {
                  text: scoreString,
                  width: '48%',
                },
              ],
              margin: [5, 5, 0, 5],
            },
            {
              table: {
                body: ingredientsBody,
              },
            },
          ],
        },
        {
          stack: [
            { image: dataRow.dropImage.wellImage, height: 130, width: 170 },
            {
              text:
                new Date(dataRow.dateImaged).toLocaleTimeString() +
                ' ' +
                new Date(dataRow.dateImaged).toLocaleDateString('en-US'),
              alignment: 'center',
            },
          ],
        },
      ]);
    }
  } else {
    tableBody.push('');
  }
  return {
    table: {
      widths: ['64%', '36%'],
      body: tableBody,
    },
    layout: TABLE_LAYOUT,
  };
}

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

export default async function pdfInstantDropReport(
  selectedWell: WellNavigationDetails[],
  docData,
  dropsChecked,
  selectedDropImageParameters,
  saveReport,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: docData.expName + '-Instant Drop Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(
      selectedWell,
      docData,
      dropsChecked,
      selectedDropImageParameters,
    ),
    styles: {
      titleText: { fontSize: 20, bold: true, lineHeight: 1.5 },
      headerText: { fontSize: 15, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 12, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      experimentLabelText: { fontSize: 12, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 9,
      lineHeight: 1.2,
    },
    pageMargins: [50, 50, 50, 30],
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    docData.expName + ' - InstantDropReport',
    saveReport,
    onPopupBlocked,
  );
}

function getScreenNameColumns(screenNames: string[]) {
  return getScreenNameColumnsTable(
    screenNames,
    'experimentLabelText',
    INSTANT_DROP_SCREEN_NAME_SHORT_MAX_LENGTH,
    INSTANT_DROP_SCREEN_NAME_MEDIUM_MAX_LENGTH,
  );
}

async function CreateContent(
  selectedWell: WellNavigationDetails[],
  docData: any,
  dropsChecked: any,
  selectedDropImageParameters: any,
) {
  const startingWell = selectedWell.find((x) => x.selectionOrder === 0);
  const selectedPlateId = !isNullOrUndefined(startingWell)
    ? startingWell.plateID
    : selectedWell[0].plateID;

  const content = [];
  content.push('\n');
  content.push({ text: 'Instant Drop Report', alignment: 'center', style: 'titleText' });
  content.push({ text: 'Hits By Ingredients', style: 'headerText' });
  const ingredientsTableBody = getHitsByIngredients(
    selectedWell,
    docData,
    selectedPlateId,
  );
  content.push(ingredientsTableBody);
  content.push('\n');

  const sortedSelectedWell = selectedWell.filter(
    (well) => well.plateID === selectedPlateId,
  );
  sortedSelectedWell.sort((a, b) => {
    return a.wellNum - b.wellNum;
  });

  let barcode = docData.plateDto.find(
    (plate) => plate.plateId == selectedPlateId,
  ).plateBarName;
  barcode = barcode.split(',');
  if (barcode.length > 1) {
    barcode = barcode[2].trim() + ' (' + barcode[1].trim() + ')';
  }

  let imagedTime = selectedDropImageParameters?.inspection?.dateImaged;
  if (!isNullOrUndefined(imagedTime)) {
    imagedTime = dateTimeConverter(Date.parse(imagedTime));
  }

  const screenNames = getScreenNamesFromLayers(docData.layers);

  content.push({ text: 'Hits By Experiment', pageBreak: 'before', style: 'headerText' });
  content.push({
    text: [{ text: 'Experiment Name: ', bold: true }, { text: docData.expName }],
    style: 'experimentLabelText',
  });
  content.push({
    text: [{ text: 'Barcode(ID): ', bold: true }, { text: barcode }],
    style: 'experimentLabelText',
  });
  content.push({
    text: [
      { text: 'Imaged: ', bold: true },
      { text: isNullOrUndefined(imagedTime) ? 'Not Imaged' : imagedTime },
    ],
    style: 'experimentLabelText',
  });
  content.push(getScreenNameColumns(screenNames));
  content.push('\n');
  const hitsTableBody = await fetchDataHitsByExperiment(
    sortedSelectedWell,
    docData,
    dropsChecked,
    selectedDropImageParameters,
    selectedPlateId,
  );
  content.push(hitsTableBody);
  return content;
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
        },
      ],
      margin: [20, 20, 20, 20],
    },
  ];
}
